import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Info,
  Lock,
  RefreshCw,
  Unlock,
  Zap,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { rotuloTipo } from "@/lib/tipoProfissional";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { formatBRL, formatarData, nomeMes } from "@/lib/utils";
import type { RouterOutput } from "@/types";

// ============================================================
// F2 — Conferência mensal dos turnos
// ============================================================
// A tela onde o mês deixa de ser previsão. Cada turno recebe uma decisão
// (realizado, cancelado, falta) e fica com a marca de quem conferiu — porque
// a regra do Bryan é conferir COM o profissional, até o 5º dia útil, e não
// decidir sozinho no fim do mês.
//
// O que esta tela NÃO faz: pagar. Ela produz um número apurado e o congela.
// Emitir lançamento e dar baixa continua sendo do ERP da clínica.

function reais(centavos: number | null | undefined): string {
  return formatBRL((centavos ?? 0) / 100);
}

type StatusTurno = "realizado" | "cancelado" | "falta" | "agendado";

const ESTILO_STATUS: Record<string, { rotulo: string; classe: string }> = {
  realizado: { rotulo: "Realizado", classe: "bg-emerald-100 text-emerald-800" },
  cancelado: { rotulo: "Cancelado", classe: "bg-slate-200 text-slate-600" },
  falta: { rotulo: "Falta", classe: "bg-red-100 text-red-800" },
  agendado: { rotulo: "Sem conferência", classe: "bg-amber-100 text-amber-800" },
  pendente: { rotulo: "Sem conferência", classe: "bg-amber-100 text-amber-800" },
  aprovado: { rotulo: "Sem conferência", classe: "bg-amber-100 text-amber-800" },
};

export function Conferencia() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);

  const utils = trpc.useUtils();
  const q = trpc.conferencia.listar.useQuery({ ano, mes, incluirInativos });

  const abrirMes = trpc.conferencia.abrir.useMutation({
    onSuccess: (r) => {
      utils.conferencia.listar.invalidate();
      alert(
        `Competência aberta: ${r.criadas} conferência(s) criada(s), ${r.jaExistiam} já existia(m).`,
      );
    },
    onError: (e) => alert(e.message),
  });

  const itens = q.data?.itens ?? [];
  const totais = q.data?.totais;

  const ordenados = useMemo(() => {
    return [...itens].sort((a, b) => {
      // Quem ainda tem turno sem decisão sobe: é o trabalho que falta.
      const aFalta = a.resumo.pendentes > 0 ? 0 : 1;
      const bFalta = b.resumo.pendentes > 0 ? 0 : 1;
      if (aFalta !== bFalta) return aFalta - bFalta;
      return a.nome.localeCompare(b.nome, "pt-BR");
    });
  }, [itens]);

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) {
      novo = 12;
      novoAno--;
    }
    if (novo > 12) {
      novo = 1;
      novoAno++;
    }
    setMes(novo);
    setAno(novoAno);
    setAberto(null);
  }

  // Quem nao tem turno no mes nao entra na conferencia: nao ha o que conferir.
  const comTurnos = ordenados.filter((i) => i.resumo.qtd > 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Conferência mensal</h1>
          <p className="text-sm text-slate-600">
            O que aconteceu de verdade em cada turno, conferido com cada profissional.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={incluirInativos}
              onChange={(e) => setIncluirInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => abrirMes.mutate({ ano, mes })}
            disabled={abrirMes.isPending}
          >
            Abrir competência
          </Button>
          <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
          {nomeMes(mes)} {ano}
        </div>
        <Button variant="outline" size="icon" onClick={() => navegar(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          Fechar a conferência congela o valor apurado daquele mês e tranca os turnos da pessoa:
          só um administrador reabre, com motivo. Nada aqui paga ninguém — o pagamento continua
          sendo feito pelo sistema da clínica.
        </span>
      </div>

      {q.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {q.error.message}
        </div>
      )}

      {q.isLoading && <div className="py-10 text-center text-slate-500">Carregando turnos…</div>}

      {!q.isLoading && !q.error && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Turnos no mês" value={String(totais?.turnos ?? 0)} />
            <Stat
              label="Sem conferência"
              value={String(totais?.pendentes ?? 0)}
              alerta={(totais?.pendentes ?? 0) > 0}
            />
            <Stat label="Previsto" value={reais(totais?.previstoCentavos)} />
            <Stat label="Apurado" value={reais(totais?.apuradoCentavos)} destaque />
          </div>

          <div className="space-y-3">
            {comTurnos.map((i) => (
              <BlocoProfissional
                key={i.profissionalId}
                item={i}
                ano={ano}
                mes={mes}
                aberto={aberto === i.profissionalId}
                onToggle={() =>
                  setAberto(aberto === i.profissionalId ? null : i.profissionalId)
                }
              />
            ))}
            {comTurnos.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center text-slate-400">
                  Nenhum turno lançado nesta competência.
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type Item = RouterOutput["conferencia"]["listar"]["itens"][number];

function BlocoProfissional({
  item,
  ano,
  mes,
  aberto,
  onToggle,
}: {
  item: Item;
  ano: number;
  mes: number;
  aberto: boolean;
  onToggle: () => void;
}) {
  const { isAdmin } = usePermissions();
  const utils = trpc.useUtils();
  const conferido = item.conferencia?.status === "conferido";

  function invalidar() {
    utils.conferencia.listar.invalidate();
    utils.comissoes.resumoPorProfissional.invalidate();
    utils.turnos.listarPorMes.invalidate();
  }

  const marcarTurno = trpc.conferencia.marcarTurno.useMutation({
    onSuccess: invalidar,
    onError: (e) => alert(e.message),
  });
  const marcarLote = trpc.conferencia.marcarLote.useMutation({
    onSuccess: (r) => {
      invalidar();
      if (r.recusados.length > 0) {
        alert(`${r.marcados} marcado(s). ${r.recusados.length} recusado(s).`);
      }
    },
    onError: (e) => alert(e.message),
  });
  const fechar = trpc.conferencia.fechar.useMutation({
    onSuccess: invalidar,
    onError: (e) => alert(e.message),
  });
  const reabrir = trpc.conferencia.reabrir.useMutation({
    onSuccess: invalidar,
    onError: (e) => alert(e.message),
  });

  const ocupado =
    marcarTurno.isPending || marcarLote.isPending || fechar.isPending || reabrir.isPending;

  function marcarTodosPendentes() {
    const ids = item.turnos.filter((t) => t.status === "agendado").map((t) => t.turnoId);
    if (ids.length === 0) return;
    if (
      !confirm(
        `Marcar ${ids.length} turno(s) de ${item.nome} como realizado? Faça isso depois de conferir a lista com a pessoa.`,
      )
    )
      return;
    marcarLote.mutate({ turnoIds: ids, status: "realizado" });
  }

  function fecharConferencia() {
    const resumo = `${item.resumo.realizados} realizado(s), ${item.resumo.cancelados} cancelado(s), ${item.resumo.faltas} falta(s)`;
    if (
      !confirm(
        `Fechar a conferência de ${item.nome} em ${String(mes).padStart(2, "0")}/${ano}?\n\n` +
          `${resumo}\nApurado: ${reais(item.resumo.apuradoCentavos)}\n\n` +
          "Depois disso os turnos do mês ficam trancados até um administrador reabrir.",
      )
    )
      return;
    fechar.mutate({ ano, mes, profissionalId: item.profissionalId });
  }

  function reabrirConferencia() {
    const motivo = prompt(`Por que reabrir a conferência de ${item.nome}?`);
    if (!motivo || motivo.trim().length < 5) return;
    reabrir.mutate({ ano, mes, profissionalId: item.profissionalId, motivo: motivo.trim() });
  }

  return (
    <Card className={conferido ? "border-emerald-200" : ""}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <button className="flex flex-1 items-start gap-3 text-left" onClick={onToggle}>
            <span className="mt-1 text-slate-400">
              {aberto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </span>
            <span>
              <span className="block font-medium text-slate-900">{item.nome}</span>
              <span className="mt-1 flex flex-wrap items-center gap-1">
                <Badge variant="outline">{rotuloTipo(item.tipo)}</Badge>
                {!item.ativo && <Badge variant="inativo">inativo</Badge>}
                {conferido ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                    <Lock className="h-3 w-3" /> conferido
                  </span>
                ) : item.resumo.pendentes > 0 ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    {item.resumo.pendentes} sem conferência
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                    pronto para fechar
                  </span>
                )}
                {item.conferencia?.reabertoEm && (
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                    reaberta
                  </span>
                )}
              </span>
            </span>
          </button>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <Numero rotulo="Turnos" valor={String(item.resumo.qtd)} />
            <Numero rotulo="Realizados" valor={String(item.resumo.realizados)} />
            <Numero rotulo="Previsto" valor={reais(item.resumo.previstoCentavos)} />
            <Numero
              rotulo={conferido ? "Apurado (fechado)" : "Apurado"}
              valor={reais(
                conferido
                  ? item.conferencia?.totalApuradoCentavos
                  : item.resumo.apuradoCentavos,
              )}
              destaque
            />
          </div>
        </div>

        {aberto && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            {conferido && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <span>
                  Conferência fechada
                  {item.conferencia?.conferidoEm
                    ? ` em ${new Date(item.conferencia.conferidoEm).toLocaleString("pt-BR")}`
                    : ""}{" "}
                  · {item.conferencia?.qtdTurnosApurados ?? 0} turno(s) ·{" "}
                  {reais(item.conferencia?.totalApuradoCentavos)}. Os turnos do mês estão
                  trancados.
                </span>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={reabrirConferencia}
                    disabled={ocupado}
                  >
                    <Unlock className="mr-2 h-4 w-4" /> Reabrir
                  </Button>
                )}
              </div>
            )}

            {item.conferencia?.reabertoMotivo && (
              <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900">
                Reaberta: {item.conferencia.reabertoMotivo}
              </div>
            )}

            {!conferido && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={marcarTodosPendentes}
                  disabled={ocupado || item.resumo.pendentes === 0}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Marcar os {item.resumo.pendentes} pendentes como realizados
                </Button>
                <Button
                  size="sm"
                  onClick={fecharConferencia}
                  disabled={ocupado || item.resumo.qtd === 0}
                >
                  <Lock className="mr-2 h-4 w-4" /> Fechar conferência
                </Button>
              </div>
            )}

            <ListaTurnos
              turnos={item.turnos}
              travado={conferido || ocupado}
              onMarcar={(turnoId, status) => marcarTurno.mutate({ turnoId, status })}
              onAcionar={(turnoId, acionado, status) =>
                marcarTurno.mutate({ turnoId, status, acionado })
              }
              onObservar={(turnoId, status, observacao) =>
                marcarTurno.mutate({ turnoId, status, observacao })
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ListaTurnos({
  turnos,
  travado,
  onMarcar,
  onAcionar,
  onObservar,
}: {
  turnos: Item["turnos"];
  travado: boolean;
  onMarcar: (turnoId: number, status: StatusTurno) => void;
  onAcionar: (turnoId: number, acionado: boolean, status: StatusTurno) => void;
  onObservar: (turnoId: number, status: StatusTurno, observacao: string) => void;
}) {
  if (turnos.length === 0) {
    return <div className="py-4 text-sm text-slate-400">Nenhum turno no mês.</div>;
  }

  return (
    <ul className="divide-y divide-slate-100">
      {turnos.map((t) => {
        const status = (t.status as StatusTurno) ?? "agendado";
        const estilo = ESTILO_STATUS[t.status] ?? ESTILO_STATUS.agendado!;
        const statusAtual: StatusTurno =
          status === "realizado" || status === "cancelado" || status === "falta"
            ? status
            : "agendado";
        return (
          <li key={t.turnoId} className="flex flex-col gap-2 py-3 lg:flex-row lg:items-center">
            <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: t.tipoTurnoCor }}
              />
              <span className="font-medium text-slate-800">{formatarData(t.data)}</span>
              <span className="text-slate-600">
                {t.tipoTurnoNome} · {t.horaInicio}–{t.horaFim}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${estilo.classe}`}>
                {estilo.rotulo}
              </span>
              {t.acionado && (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                  <Zap className="h-3 w-3" /> acionado
                </span>
              )}
              {t.observacaoConferencia && (
                <span
                  className="truncate text-xs italic text-slate-500"
                  title={t.observacaoConferencia}
                >
                  {t.observacaoConferencia}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-24 text-right text-sm font-medium text-slate-900">
                {t.valorApuradoCentavos !== null
                  ? reais(t.valorApuradoCentavos)
                  : reais(t.valorHojeCentavos)}
              </span>

              <div className="flex overflow-hidden rounded-md border border-slate-300">
                <BotaoStatus
                  ativo={statusAtual === "realizado"}
                  rotulo="Realizado"
                  cor="emerald"
                  disabled={travado}
                  onClick={() => onMarcar(t.turnoId, "realizado")}
                />
                <BotaoStatus
                  ativo={statusAtual === "cancelado"}
                  rotulo="Cancelado"
                  cor="slate"
                  disabled={travado}
                  onClick={() => onMarcar(t.turnoId, "cancelado")}
                />
                <BotaoStatus
                  ativo={statusAtual === "falta"}
                  rotulo="Falta"
                  cor="red"
                  disabled={travado}
                  onClick={() => onMarcar(t.turnoId, "falta")}
                />
              </div>

              {t.acionavel && (
                <label
                  className="flex items-center gap-1 text-xs text-slate-600"
                  title={`Sobreaviso acionado paga ${reais(t.valorAcionadoCentavos)}`}
                >
                  <input
                    type="checkbox"
                    checked={t.acionado}
                    disabled={travado}
                    onChange={(e) => onAcionar(t.turnoId, e.target.checked, statusAtual)}
                  />
                  acionado
                </label>
              )}

              <button
                type="button"
                className="text-xs text-slate-500 underline-offset-2 hover:underline disabled:opacity-40"
                disabled={travado}
                onClick={() => {
                  const obs = prompt(
                    "Observação da conferência (o que foi combinado com o profissional):",
                    t.observacaoConferencia ?? "",
                  );
                  if (obs === null) return;
                  onObservar(t.turnoId, statusAtual, obs);
                }}
              >
                observação
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BotaoStatus({
  ativo,
  rotulo,
  cor,
  disabled,
  onClick,
}: {
  ativo: boolean;
  rotulo: string;
  cor: "emerald" | "slate" | "red";
  disabled?: boolean;
  onClick: () => void;
}) {
  const ativoClasse =
    cor === "emerald"
      ? "bg-emerald-600 text-white"
      : cor === "red"
        ? "bg-red-600 text-white"
        : "bg-slate-600 text-white";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        ativo ? ativoClasse : "bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {rotulo}
    </button>
  );
}

function Numero({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <span className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">{rotulo}</span>
      <span className={`font-semibold ${destaque ? "text-brand-600" : "text-slate-900"}`}>
        {valor}
      </span>
    </span>
  );
}

function Stat({
  label,
  value,
  destaque,
  alerta,
}: {
  label: string;
  value: string;
  destaque?: boolean;
  alerta?: boolean;
}) {
  return (
    <Card className={alerta ? "border-amber-200" : ""}>
      <CardContent className="p-5">
        <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          {alerta && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
          {label}
        </div>
        <div
          className={`mt-1 text-2xl font-bold ${
            destaque ? "text-brand-600" : alerta ? "text-amber-700" : "text-slate-900"
          }`}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
